import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Mail, Phone, MapPin } from "lucide-react";
import { api } from "@/lib/apiClient";

const OFFICE_IMG = "https://images.pexels.com/photos/7654401/pexels-photo-7654401.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent", { description: "We'll reply from Berlin within 1 business day." });
      setDone(true);
    } catch (err) {
      toast.error("Failed to send", { description: err?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">Contact</div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-[#1A2E24]">
          Talk to Berlin.
        </h1>
        <p className="mt-6 text-[#5C6B64] leading-relaxed">
          Questions about contracts, visas, or joining the network? Write us — a real human in
          our Torstraße office reads every message.
        </p>

        <ul className="mt-10 space-y-4 text-sm text-[#1A2E24]">
          <li className="flex items-start gap-3"><MapPin className="text-[#D35400] mt-0.5" size={18}/>Torstraße 138, 10119 Berlin, Germany</li>
          <li className="flex items-start gap-3"><Mail className="text-[#D35400] mt-0.5" size={18}/>hello@toolcoolhomestead.com</li>
          <li className="flex items-start gap-3"><Phone className="text-[#D35400] mt-0.5" size={18}/>+49 30 1234 5678</li>
        </ul>

        <div className="mt-10 relative aspect-[4/3] overflow-hidden border border-[#DCD7C9]">
          <img src={OFFICE_IMG} alt="Berlin office" className="h-full w-full object-cover"/>
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="border border-[#DCD7C9] bg-white p-6 sm:p-10">
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#2E4E3F]/10 text-[#2E4E3F] mb-4"><Check size={26}/></div>
              <div className="font-display text-2xl text-[#1A2E24]">Message sent</div>
              <p className="mt-2 text-sm text-[#5C6B64]">We'll reply from Berlin shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#5C6B64]">Name</label>
                <input data-testid="contact-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-1 w-full border border-[#DCD7C9] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#5C6B64]">Email</label>
                <input data-testid="contact-email" required type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="mt-1 w-full border border-[#DCD7C9] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-[#5C6B64]">Subject</label>
                <input data-testid="contact-subject" required value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} className="mt-1 w-full border border-[#DCD7C9] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-[#5C6B64]">Message</label>
                <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} className="mt-1 w-full border border-[#DCD7C9] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"/>
              </div>
              <div className="sm:col-span-2">
                <button data-testid="contact-submit" disabled={busy} className="w-full bg-[#1A2E24] text-[#F9F6F0] py-3.5 text-sm font-medium hover:bg-[#2C4A3B] transition-colors disabled:opacity-60">
                  {busy ? "Sending…" : "Send message"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
